// BookTickets/stages/QuantitySelection/QuantitySelection.tsx
'use client'

import { useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'
import cn from 'classnames'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import ContinueButton from '@booking/components/ContinueButton'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { TicketQuantities } from '@booking/types/booking'
import type { TicketVariant } from '@booking/types/bookingConfig'
import { variantTranslationKey } from '@booking/lib/bookingConfig'

import TicketType from './components/TicketType'
import AdultRestrictionsModal from './components/AdultRestrictionsModal'
import RestrictionsModal from './components/RestrictionsModal'
import GroupSizeModal from './components/GroupSizeModal'
import AgeRestrict from './components/AgeRestrict'

export default function QuantitySelection() {
  const t = useTranslations('booking')

  const state = useBookingState()
  const { dispatch, onCompleteQuantitySelection } = useBookingActionsCtx()

  const ticketTypes = (state.config?.ticketTypes ?? []) as TicketVariant[]
  const maxTickets = state.config?.maxTicketsPerBooking ?? 9
  const hasExtras = Boolean(state.config?.extras)
  const extrasTotal = state.extras.reduce((sum, e) => sum + e.quantity, 0)
  const ticketCount = state.quantities.reduce((sum, q) => sum + q.quantity, 0)

  const [showError, setShowError] = useState(false)
  const [adultModalIsOpen, setAdultModalIsOpen] = useState(false)
  const [restrictionsModalIsOpen, setRestrictionsModalIsOpen] = useState(false)
  const [groupSizeModalIsOpen, setGroupSizeModalIsOpen] = useState(false)

  /*const allowContinue = useMemo(() => {
    // keep reducer as source of truth, but guard if allowContinue isn't set yet
    return Boolean(state.allowContinue) && ticketCount(state.quantities) > 0
  }, [state.quantities, state.allowContinue])*/

  const setNewValue = useCallback(
    (variant: TicketVariant, nextCount: number) => {
      const prevItem = state.quantities.find((item) => item.category === variant)
      if (prevItem?.quantity === nextCount) return

      // Create a new array with updated quantity
      const nextCounts: TicketQuantities = state.quantities.map((item) =>
        item.category === variant ? { ...item, quantity: nextCount } : item,
      )

      // If the variant doesn't exist yet, add it
      if (!nextCounts.find((item) => item.category === variant)) {
        nextCounts.push({ category: variant, quantity: nextCount })
      }

      dispatch({ type: 'SET_TICKET_QUANTITIES', quantities: nextCounts })

      if (showError) setShowError(false)
    },
    [dispatch, state.quantities, showError, hasExtras, extrasTotal],
  )

  const validate = useCallback(() => {
    const anySelected = state.quantities.some((item) => item.quantity > 0)
    const currentTicketCount = state.quantities.reduce((sum, q) => sum + q.quantity, 0)
    const currentExtrasTotal = state.extras.reduce((sum, e) => sum + e.quantity, 0)

    if (!anySelected) {
      setShowError(true)
      return false
    }

    if (hasExtras && currentTicketCount !== currentExtrasTotal) {
      setShowError(true)
      return false
    }

    return true
  }, [state.quantities, state.extras, hasExtras])

  const onContinue = useCallback(() => {
    if (!validate()) return
    return onCompleteQuantitySelection()
  }, [onCompleteQuantitySelection, validate])

  return (
    <div className={cn(shared.box)}>
      {/* Modals */}
      <AdultRestrictionsModal open={adultModalIsOpen} onOpenChange={setAdultModalIsOpen} />
      <RestrictionsModal open={restrictionsModalIsOpen} onOpenChange={setRestrictionsModalIsOpen} />
      <GroupSizeModal open={groupSizeModalIsOpen} onOpenChange={setGroupSizeModalIsOpen} />

      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          <div className={cn(styles.header)}>
            <div>
              <BackButton />
              <h2 className={cn(styles.title)}>
                {t(
                  hasExtras
                    ? 'ticket.selectQuantity.ticketsSectionTitle'
                    : 'ticket.selectQuantity.title',
                )}
              </h2>

              <div className={cn(styles.note)}>
                {t.rich(state.config?.text?.quantityDesc2Key ?? 'ticket.selectQuantity.desc2', {
                  contact: (chunks) => <a href="mailto:booking@lisbonquake.com">{chunks}</a>,
                })}
              </div>
            </div>
          </div>

          <div className={cn(styles.boxComandsWrapper)}>
            {showError ? (
              <div className={styles.errorHolder}>
                {hasExtras && ticketCount !== extrasTotal
                  ? t('ticket.selectQuantity.extrasError')
                  : t('ticket.selectQuantity.error')}
              </div>
            ) : null}

            {hasExtras ? (
              <div className={styles.ticketsSectionHeader}>
                <span
                  className={cn(
                    styles.ticketsHelper,
                    ticketCount === extrasTotal && styles.ticketsHelperDone,
                  )}
                >
                  {t('ticket.selectQuantity.ticketsHelper', {
                    selected: ticketCount,
                    total: extrasTotal,
                  })}
                </span>
              </div>
            ) : null}

            <ul className={styles.ticketTypes}>
              {ticketTypes.map((type) => {
                const value = state.quantities.find((item) => item.category === type)?.quantity ?? 0

                return (
                  <li id={`ticket-${type}`} key={type}>
                    <div>
                      <span className={cn(styles.variant)}>
                        {t(`ticket.selectQuantity.${variantTranslationKey(type)}`)}
                      </span>

                      {variantTranslationKey(type) === 'adult' ? (
                        <AgeRestrict transKey="adult" onMore={() => setAdultModalIsOpen(true)} />
                      ) : null}

                      {variantTranslationKey(type) === 'child' ? (
                        <AgeRestrict
                          transKey="child"
                          onMore={() => setRestrictionsModalIsOpen(true)}
                        />
                      ) : null}

                      {type === 'child-hh' ? <AgeRestrict transKey="child-hh" /> : null}
                    </div>

                    <TicketType
                      variant={type}
                      value={value}
                      onChange={(next) => setNewValue(type, next)}
                      onExceedMax={() => setGroupSizeModalIsOpen(true)}
                      maxGlobal={hasExtras ? extrasTotal : maxTickets}
                      disableAtGlobalMax={hasExtras}
                    />
                  </li>
                )
              })}
            </ul>
          </div>

          <ContinueButton className={cn(shared.continue)} onContinue={onContinue} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={onContinue} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}
